import { useState } from "react";
import { motion } from "framer-motion";
import { PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { CONTACT } from "@/constants/testIds";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", inquiryType: "consulting" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const inquiryTypes = [
    { value: "consulting", label: "Consulting Engagement" },
    { value: "training", label: "Training Proposal" },
    { value: "apps", label: "App Demo (Mitti)" },
  ];

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: "", email: "", company: "", message: "", inquiryType: "consulting" });
    } catch (err) {
      console.error("Contact form error:", err?.response?.data || err?.message || err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    backgroundColor: "var(--sv-bg-input)",
    borderColor: "var(--sv-border)",
    color: "var(--sv-text-primary)",
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.section}
      className="relative py-24 sm:py-32 lg:py-40"
    >
      <div className="absolute inset-0 bg-themed-secondary z-0" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p data-testid={CONTACT.overline} className="overline mb-4">Get In Touch</p>
            <h2
              data-testid={CONTACT.heading}
              className="text-4xl sm:text-5xl font-bold tracking-tight mb-6 text-themed-primary"
              style={{ fontFamily: "var(--sv-heading-font)" }}
            >
              Ready to Eliminate Process Drag?
            </h2>
            <p className="text-themed-body text-base sm:text-lg leading-relaxed mb-10 max-w-lg">
              Book a free thirty-minute consultation to discuss your HR or People department
              and discover how the Siveli Master Diagnostic Engine can quantify your Process Drag.
            </p>
            <div className="space-y-4 text-sm text-themed-muted">
              <div className="flex items-center gap-3">
                <span className="accent-dot" />
                <span>Free 30-minute diagnostic consultation</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="accent-dot" />
                <span>Evidence-led recommendations, not generic advice</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="accent-dot" />
                <span>No obligation, no sales pressure</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            {success ? (
              <div
                data-testid={CONTACT.successMessage}
                className="glass-card p-10 flex flex-col items-center text-center"
              >
                <CheckCircle size={48} weight="duotone" className="text-themed-gold mb-4" />
                <h3 className="text-2xl font-semibold text-themed-primary mb-2" style={{ fontFamily: "var(--sv-heading-font)" }}>Inquiry Received</h3>
                <p className="text-themed-body text-sm">We'll be in touch within 24 hours to schedule your consultation.</p>
              </div>
            ) : (
              <form data-testid={CONTACT.form} onSubmit={handleSubmit} className="glass-card p-8 sm:p-10 space-y-6">
                <div>
                  <label className="block text-xs text-themed-muted tracking-wider uppercase mb-2" style={{ fontFamily: "var(--sv-mono-font)" }}>Name *</label>
                  <input
                    data-testid={CONTACT.nameInput}
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full border px-4 py-3 text-sm outline-none transition-colors"
                    style={inputStyle}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-themed-muted tracking-wider uppercase mb-2" style={{ fontFamily: "var(--sv-mono-font)" }}>Email *</label>
                  <input
                    data-testid={CONTACT.emailInput}
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full border px-4 py-3 text-sm outline-none transition-colors"
                    style={inputStyle}
                    placeholder="your@company.com"
                  />
                </div>
                <div>
                  <label className="block text-xs text-themed-muted tracking-wider uppercase mb-2" style={{ fontFamily: "var(--sv-mono-font)" }}>Company</label>
                  <input
                    data-testid={CONTACT.companyInput}
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full border px-4 py-3 text-sm outline-none transition-colors"
                    style={inputStyle}
                    placeholder="Company name"
                  />
                </div>
                <div>
                  <label className="block text-xs text-themed-muted tracking-wider uppercase mb-2" style={{ fontFamily: "var(--sv-mono-font)" }}>I'm interested in *</label>
                  <div className="flex flex-wrap gap-2">
                    {inquiryTypes.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        data-testid={`inquiry-type-${t.value}`}
                        onClick={() => setForm({ ...form, inquiryType: t.value })}
                        className="px-4 py-2 text-sm transition-all duration-200"
                        style={{
                          border: "1px solid",
                          borderColor: form.inquiryType === t.value ? "var(--sv-gold)" : "var(--sv-border)",
                          background: form.inquiryType === t.value ? "var(--sv-gold)" : "transparent",
                          color: form.inquiryType === t.value ? "var(--sv-bg-primary)" : "var(--sv-text-muted)",
                          fontWeight: form.inquiryType === t.value ? 600 : 400,
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-themed-muted tracking-wider uppercase mb-2" style={{ fontFamily: "var(--sv-mono-font)" }}>Message *</label>
                  <textarea
                    data-testid={CONTACT.messageInput}
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full border px-4 py-3 text-sm outline-none transition-colors resize-none"
                    style={inputStyle}
                    placeholder="Tell us about your operational challenges..."
                  />
                </div>

                {error && <p className="text-red-400 text-sm">{error}</p>}

                <button
                  data-testid={CONTACT.submitBtn}
                  type="submit"
                  disabled={submitting}
                  className="w-full btn-shine font-bold tracking-wide py-4 text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: "var(--sv-gold)", color: "var(--sv-bg-primary)" }}
                >
                  <PaperPlaneTilt size={18} weight="bold" />
                  {submitting ? "Sending..." : "Send Inquiry"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
